import './index.css';

import { upgradeScanLoaderLabel } from 'utils/scanLoader';

import App from './App';

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { MemoryRouter } from 'react-router-dom';

import { SnackbarProvider } from 'components/Snackbar';
import Sprite from 'components/Sprite';

import { store } from 'store';

import { CssBaseline, StyledEngineProvider, ThemeProvider } from '@mui/material';
import { appTheme } from 'settings/theme';
import { QueryProvider } from './providers/queryClient';
import { PreviewContextProvider } from 'contexts/previewContext';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { CompanyContextProvider } from 'contexts/companyContext';

// FIRST statement in the entry module, ahead of the shadow-root setup that can throw:
// the loader is already on screen, and this is the earliest point at which anything
// knows whether a verification is actually happening. It is a no-op without a `vt`, and
// the label it upgrades stays invisible until 1200ms, so being this early is what makes
// the change unobservable. See utils/scanLoader.
upgradeScanLoaderLabel();

// Referência global para o Shadow DOM
let shadowRootRef: ShadowRoot | null = null;

export function getShadowRoot() {
  return shadowRootRef;
}

// Get the existing host element from the page.
//
// `vera-installation` is the current id. `vera-shadow-host` is the LEGACY one and
// the fallback CANNOT be dropped on a schedule of our choosing: the install
// snippet is pasted by hand into a brand's storefront (and, on the Shopify path,
// rendered into a page once at install time and never re-rendered). Every
// installation that predates the rename still says `vera-shadow-host`, on servers
// we do not control. Removing the fallback would not degrade those pages, it
// would blank them — the throw below is what the certificate renders instead.
// Retire it only once no live installation reports the legacy id.
const host = (document.getElementById('vera-installation') ??
  document.getElementById('vera-shadow-host')) as HTMLElement;

if (!host) {
  throw new Error('Widget container element with id "vera-installation" not found');
}

const decoyTag = document.createElement('p');
decoyTag.style.display = 'block !important';
host.appendChild(decoyTag);

// Attach the shadow DOM to the host element
const shadow = host.attachShadow({ mode: 'open' });

// Store the reference to the shadow DOM
shadowRootRef = shadow;

// Add a base tag to ensure URLs are resolved correctly
// This is crucial for Shadow DOM to resolve relative URLs properly
const baseTag = document.createElement('base');
// Use the document's base URI or fallback to current location
const baseHref = document.baseURI || `${window.location.origin}${window.location.pathname}`;
baseTag.href = baseHref;
shadow.appendChild(baseTag);

// CRITICAL: Add CSS reset to block inherited styles from host page (e.g., Shopify)
// This must be the FIRST style to prevent host page styles from bleeding into our widget
const baseFontStyle = document.createElement('style');
baseFontStyle.id = 'base-font-style';
baseFontStyle.textContent = `
    :host {
      --font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-size: 16px !important;
    }
  `;
shadow.appendChild(baseFontStyle);

document.querySelectorAll('style').forEach(styleTag => {
  if (styleTag.hasAttribute('data-shopify')) return;

  const clonedStyle = styleTag.cloneNode(true);
  shadow.appendChild(clonedStyle);
});

document.querySelectorAll('link[rel="stylesheet"]').forEach(linkTag => {
  const linkElement = linkTag as HTMLLinkElement;

  const isGoogleFont = linkElement.href.includes('fonts.googleapis.com');
  const isGCP = linkElement.href.includes('storage.googleapis.com');
  const isStyleCss = linkElement.href.includes('/assets/style.css');

  if (isGoogleFont || isGCP || isStyleCss) {
    const clonedLink = linkTag.cloneNode(true);
    shadow.appendChild(clonedLink);
  }
});

// Observa novos estilos sendo adicionados (importante para dev mode)
const observer = new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    mutation.addedNodes.forEach(node => {
      if (
        node.nodeName === 'STYLE' ||
        (node.nodeName === 'LINK' && (node as HTMLLinkElement).rel === 'stylesheet')
      ) {
        const cloned = node.cloneNode(true);
        shadow.appendChild(cloned);
      }
    });
  });
});

observer.observe(document.head, { childList: true });

const muiCache = createCache({
  key: 'mui',
  prepend: true,
  container: shadow, // Inject MUI styles into the shadow DOM
  speedy: false, // Disable speedy mode for the Shadow DOM
});

// Create the root React element inside the shadow DOM
const rootElement = document.createElement('div');
rootElement.id = 'vera-shadow-root';
shadow.appendChild(rootElement);

// VERA-837: the app uses MemoryRouter (URL bar does not drive routing — it is
// embedded-first, so internal navigation must not change the parent/iframe URL).
// Standalone entry links opened from an email (create-account / claim invites,
// self-serve signup, sign-in) arrive as a real path in the browser, so seed the
// in-memory history from that path ONLY when it is a known standalone route.
// Everything else keeps the default "/" start (preserves embedded behavior).
// '/sign-in' now renders the redesigned SignInBanner directly (no longer the
// legacy SignIn page), so it is seeded here too — a bookmarked/shared
// '/sign-in' link resolves straight to the sign-in surface.
// VRG-268: '/tag-replaced' is a STANDALONE entry by necessity — it is only ever reached by a
// redirect from `GET /verify/:uid` after someone taps a retired tag. Without it in this list
// MemoryRouter ignores the path and starts at '/', which redirects to '/certificates' and then
// bounces an unauthenticated stranger to the SIGN-IN page. That is worse than the bare 404 this
// whole flow exists to replace: it demands credentials from someone who just tapped an object.
// Caught by loading the route in a browser; the route registration in App.tsx looks correct in
// isolation and this is invisible to typecheck and to code review.
// VRG-275: '/tag-not-active' is the same trap as '/tag-replaced' above — it is only ever
// reached by a redirect from `GET /verify/:uid` after someone taps a HELD replacement tag,
// so it must be seeded here too or MemoryRouter strands them on the sign-in page.
// VRG-319: '/certificate-not-active' is the third instance of the same trap — reached only
// by a redirect from `GET /verify/:uid` when the tag is genuine but its certificate was
// never activated. Omitting it here is invisible to typecheck and to review (the route in
// App.tsx looks correct in isolation) and would send a stranger who tapped a physical
// object to a SIGN-IN page. '/invalid-scan' WAS the standing proof this is not theoretical
// — registered in App.tsx, missing from this list, unreachable — and is now fixed below.
const STANDALONE_ENTRY_PATHS = [
  '/create-account',
  '/claim',
  '/signup',
  '/sign-in',
  '/tag-replaced',
  '/tag-not-active',
  '/certificate-not-active',
  // The instance the note above cited as still broken. Registered at `App.tsx:556`,
  // absent here, so it booted at '/' and bounced a stranger who tapped an object to
  // sign-in — the exact failure the three lines above exist to prevent. Verified before
  // fixing: present in App.tsx, zero occurrences in this list. Fixed rather than
  // documented a second time; a comment naming a live one-line bug is not a control.
  '/invalid-scan',
  // '/something-went-wrong' is the same trap again, and the one where getting it wrong costs
  // the most: it is reached ONLY by a redirect from `GET /verify/:global_tag_id` when the
  // server hit an error nobody anticipated. Omitted here, a visitor whose tap already failed
  // once would be bounced to SIGN-IN, so the page built to reassure them that their item is
  // fine would instead demand credentials from someone who just tapped an object. The
  // `?ref=` correlation id survives because `initialEntries` below appends
  // `window.location.search` to the seeded path.
  '/something-went-wrong',
  // VRG-358: '/u/<code>' is the fourth instance of the trap described above.
  // It is reached ONLY as a real browser URL, because that is the entire point
  // of it — someone pointed a phone camera at a printed/screenshotted QR. If it
  // is missing here, MemoryRouter ignores the pathname, boots at '/', and the
  // scan lands on a sign-in page instead of the person's card.
  '/u/',
];
/**
 * VRG-720: `/certificates/<id>` is the FIFTH instance of the trap the notes above
 * document four times, and the first one reached from an email WE send.
 * `send_claim_on_scan_owned` — the "You now own <product>" email — builds its
 * `actionUrl` as `${host}/certificates/${certificate.id}`
 * (`notification.sendgrid.service.ts:1804`), so clicking that link seeded nothing,
 * booted MemoryRouter at '/', and landed the new owner on their LIST, or on
 * sign-in, instead of the certificate the email had just told them was theirs.
 * Found by opening the URL; invisible to typecheck and to code review, exactly as
 * the four notes above warn.
 *
 * Matched by SHAPE rather than by prefix, unlike every entry in the list, and the
 * difference matters: this app is embedded-first and the embed runs on a brand's
 * own storefront, where `/certificates/...` is a plausible page OF THE BRAND'S OWN
 * — `/u/` and `/tag-replaced` are not. A bare prefix would let a storefront's URL
 * drive the widget's router. A certificate id is a uuid, so requiring one admits
 * the email link and keeps a brand's `/certificates/winter-collection` out.
 */
const CERTIFICATE_ENTRY_RE =
  /^\/certificates\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/?$/i;
const currentPath = window.location.pathname;
const initialEntries =
  STANDALONE_ENTRY_PATHS.some(p => currentPath.startsWith(p)) ||
  CERTIFICATE_ENTRY_RE.test(currentPath)
    ? [currentPath + window.location.search]
    : undefined;

// Render the React app
const root = createRoot(rootElement);

root.render(
  <CacheProvider value={muiCache}>
    <Provider store={store}>
      <PreviewContextProvider>
        <CompanyContextProvider>
          <StyledEngineProvider injectFirst={false}>
            <ThemeProvider theme={appTheme}>
              <CssBaseline />
              <SnackbarProvider>
                <QueryProvider>
                  <MemoryRouter initialEntries={initialEntries}>
                    <Sprite />
                    <App />
                  </MemoryRouter>
                </QueryProvider>
              </SnackbarProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </CompanyContextProvider>
      </PreviewContextProvider>
    </Provider>
  </CacheProvider>
);
